import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    Tooltip,
    Legend,
    PointElement,
    Filler,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import React, { FC } from 'react';
import { Line } from 'react-chartjs-2';
import { colors, gutterSize, fontFamilyPrimary } from '../../theme';
import { LineChartProps } from './chartsInterfaces';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Tooltip,
    Legend,
    ChartDataLabels,
    Filler,
);

enum legendPosition {
    BOTTOM = 'bottom'
}

enum secondXAxePosition {
    TOP = 'top'
}

enum legendAlign {
    START = 'start'
}

const LineChart: FC<LineChartProps> = ({ labels, datasets }) => {
    const options = {
        indexAxis: 'x' as const,
        responsive: true,
        layout: {
            padding: {
                left: gutterSize.m,
                right: gutterSize.xxl,
            },
        },
        plugins: {
            legend: {
                display: true,
                position: legendPosition.BOTTOM,
                align: legendAlign.START,
                fullSize: true,
                labels: {
                    boxWidth: 32,
                    boxHeight: 2,
                    padding: 20,
                    color: colors.grey200,
                    font: {
                        family: fontFamilyPrimary,
                        size: 14,
                    },
                },
            },
            tooltip: {
                enabled: true,
            },
            datalabels: {
                display: true,
                color: '#fff',
                backgroundColor: '#ffffff',
                font: {
                    weight: 'bold' as const,
                    size: 11,
                },
                formatter: (value: any) => (value),
            },
        },
        scales: {
            x: {
                display: true,
                grid: {
                    display: true,
                    drawBorder: false,
                    drawTicks: true,
                    offset: false,
                    tickLength: 30,
                    color: colors.grey700,
                },
                ticks: {
                    color: colors.grey200,
                    font: {
                        size: 15,
                        family: 'Barlow',
                    },
                },
                scale: {
                    padding: 50,
                },
            },
            x2: {
                display: true,
                grid: {
                    display: true,
                    drawBorder: false,
                    drawTicks: true,
                    offset: false,
                    tickLength: 30,
                    color: colors.grey700,
                    drawLenght: 20,
                },
                ticks: {
                    display: false,
                },
                position: secondXAxePosition.TOP,
            },
            y: {
                display: true,
                grid: {
                    display: true,
                    drawBorder: false,
                    drawTicks: true,
                    offset: false,
                    tickLength: 30,
                    color: colors.grey700,
                },
                ticks: {
                    color: colors.grey200,
                    font: {
                        size: 15,
                        family: 'Barlow',
                    },
                    fontColor: colors.grey400,
                },
            },
        },
        elements: {
            point: {
                radius: 3,
            },
            line: {
                fill: true,
                borderColor: colors.blue000,
                backgroundColor: (context: any) => {
                    const bgColor = [
                        colors.blue000RGBA,
                        'rgba(255,255,255,0.25)',
                    ];
                    if (!context.chart.chartArea) {
                        return false;
                    }
                    const { ctx, chartArea: { top, bottom } } = context.chart;
                    const gradienBg = ctx.createLinearGradient(0, top, 0, bottom);
                    gradienBg.addColorStop(0, bgColor[0]);
                    gradienBg.addColorStop(1, bgColor[1]);

                    return gradienBg;
                },
            },
        },
    };

    const processedDatasets = datasets.map((dataset, index) => {
        // Alternate label positions for multiple datasets
        const positions = [
            { anchor: 'end' as const, align: 'top' as const, offset: 4 },
            { anchor: 'end' as const, align: 'bottom' as const, offset: 4 },
            { anchor: 'start' as const, align: 'top' as const, offset: 4 },
        ];

        const position = positions[index % positions.length];

        return {
            ...dataset,
            datalabels: {
                ...position,
                color: dataset.borderColor,
                backgroundColor: 'transparent',
                borderRadius: 4,
                padding: 4,
                font: {
                    weight: 'bold' as const,
                    size: 10,
                },
            },
        };
    });

    const data = {
        labels,
        datasets: processedDatasets,
    };

    return (
        <Line
            data={data}
            options={options}
        />
    );
};

export default LineChart;
